import { cn } from "@gettactic/helpers";
import { UserAvatar } from "@v2/components/display/UserAvatar/UserAvatar";
import { Button } from "@v2/components/ui/button";
import { Trash } from "lucide-react";
import { StatusSelectWithModal } from "../StatusSelectWithModal";
import { MobileStatusSelectWithModal } from "../MobileStatusSelectWithModal";
import { DetailsSideItem } from "./DetailsSideItem";
import { RequestSelect } from "./RequestSelect";
import { SidebarProps } from "./types";
import { classes } from "./utils";
import { ScheduleInfoCard, useScheduleById } from "./ScheduleInfoCard";
import { useIsMobile } from "@v2/hooks/use-mobile";

export const Sidebar = ({
  request,
  isAdmin,
  authenticatedUser,
  workplaceRequestTypes,
  pendingChanges,
  onOwnerChange,
  onStatusChange,
  onTypeChange,
  onDeleteRequest,
  formatDate,
  schedule: passedSchedule
}: SidebarProps) => {
  const isMobile = useIsMobile();
  const selectedType = workplaceRequestTypes.find(
    (t) => t.id === pendingChanges.type
  );
  const assignedUsers = selectedType?.assigned_users || [];

  // Use passed schedule if available, otherwise fetch by ID
  const { data: fetchedSchedule } = useScheduleById(
    passedSchedule ? null : request?.schedule_id || null
  );
  const schedule = passedSchedule || fetchedSchedule;
  return (
    <div className="flex w-full flex-col border-gray-200 border-l bg-gray-50 md:w-80">
      <div className="space-y-6 p-6 pb-6">
        {/* Submitter */}
        <DetailsSideItem
          label="Submitter"
          value={
            <div className="flex items-center space-x-2">
              <UserAvatar
                user={{
                  name: request.submitter_user?.name || "",
                  email: request.submitter_user?.email || "",
                  profile_photo_url: request.submitter_user?.profile_photo_url || ""
                }}
                size="sm"
                className="flex-shrink-0"
              />
              <span className="font-inter font-normal text-gray-900 text-sm">
                {request.submitter_user?.name || "Unknown"}
              </span>
            </div>
          }
        />

        {/* Display ID */}
        <DetailsSideItem label="Request ID" value={request.display_id} />

        {/* Schedule Info */}
        {schedule && (
          <DetailsSideItem
            label="Schedule"
            value={<ScheduleInfoCard schedule={schedule} className="m-0" />}
          />
        )}

        {/* Owner */}
        <DetailsSideItem
          label="Owner"
          value={
            isAdmin ? (
              <RequestSelect
                value={pendingChanges.owner}
                onValueChange={onOwnerChange}
                className="py-1 focus:ring-0"
                triggerContent={
                  <div className="flex w-full min-w-0 items-center space-x-2">
                    <UserAvatar
                      user={{
                        name: request.owner_user?.name || "?",
                        email: request.owner_user?.email || "",
                        profile_photo_url:
                          request.owner_user?.profile_photo_url || ""
                      }}
                      size={isMobile ? "s" : "sm"}
                      className="flex-shrink-0"
                    />
                    <span className="truncate font-inter font-normal text-sm">
                      {request.owner_user?.name || "No owner"}
                    </span>
                  </div>
                }
                options={[
                  {
                    id: "no-owner",
                    name: "No owner",
                    profile_photo_url: undefined
                  },
                  ...assignedUsers.map((user: any) => ({
                    id: user.id,
                    name: user.name || "?",
                    email: user.email,
                    profile_photo_url: user.profile_photo_url || ""
                  }))
                ]}
              />
            ) : (
              <div className="flex items-center space-x-2">
                <UserAvatar
                  user={{
                    name: request.owner_user?.name || "",
                    email: request.owner_user?.email || "",
                    profile_photo_url: request.owner_user?.profile_photo_url || ""
                  }}
                  size="s"
                  className="flex-shrink-0"
                />
                <span className="font-inter font-normal text-gray-900 text-sm">
                  {request.owner_user?.name || "No owner"}
                </span>
              </div>
            )
          }
        />

        {/* Status */}
        <DetailsSideItem
          label={
            <label htmlFor="status-select" className={classes.label}>
              Status
            </label>
          }
          value={
            isAdmin ? (
              <div className="w-full">
                {isMobile ? (
                  <MobileStatusSelectWithModal
                    request={request}
                    onStatusChange={onStatusChange}
                    className={cn(classes.selectTrigger, "px-4")}
                  />
                ) : (
                  <StatusSelectWithModal
                    request={request}
                    onStatusChange={onStatusChange}
                    className={cn(classes.selectTrigger, "px-4")}
                  />
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-1">
                <span className="font-normal text-gray-900 text-sm">
                  {pendingChanges.status}
                </span>
              </div>
            )
          }
        />

        {/* Workplace Request Type */}
        <DetailsSideItem
          label={
            <label htmlFor="type-select" className={classes.label}>
              Workplace Request Type
            </label>
          }
          value={
            <RequestSelect
              value={pendingChanges.type}
              onValueChange={onTypeChange}
              disabled={!isAdmin}
              className="px-4 focus:ring-0"
              triggerContent={
                <span className="font-inter font-normal text-sm">
                  {selectedType?.name || "Unknown Type"}
                </span>
              }
              options={workplaceRequestTypes.map((type) => ({
                id: type.id,
                name: type.name
              }))}
            />
          }
        />

        {/* Office */}
        <DetailsSideItem
          label="Office"
          value={
            authenticatedUser.offices?.byId[request.office_id]?.name ||
            request.office_id
          }
        />

        {/* Date Created */}
        <DetailsSideItem
          label="Date Created"
          value={formatDate(new Date(request.created))}
        />
      </div>

      {/* Action Buttons */}
      <div className="border-gray-200 border-t p-6 pt-6 pb-20 md:pb-6">
        <div className="space-y-2">
          {isAdmin && (
            <Button
              onClick={onDeleteRequest}
              variant="outline"
              size="sm"
              className="w-full justify-start border-red-200 text-red-600 hover:bg-red-50"
            >
              <Trash className="mr-2 h-4 w-4" />
              Delete Request
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
